
import { component } from '@/helpers/vueTyped';
import { ApiError } from '@/api/apiError';
import client, { MergedApi } from '@/api/server';
import cachedClient from '@/api/cachedApi';
import InlineLoader from '@/components/SharedViews/InlineLoader.vue';
import NoContentPage from '../SharedViews/NoContentPage.vue';
import Dialog from '@/components/SharedViews/Dialogs/Dialog.vue';

interface ScopedSlots {
  loaded: { response: any, isLoading?: true };
}

interface WithFetchProps {
  apiCall: keyof MergedApi;
  payload?: any[];
  returnIsLoading?: boolean;
  isCached?: boolean;
  autoUpdate?: boolean | { interval?: number, payload?: any[] };
  cacheRefreshOnUpdate?: boolean;
  hasNoContentPage?: boolean;
  hasErrorDialog?: boolean;
  fetchDelay?: number;
}

interface WithFetchEvents {
  onLoad: any;
}

type AnyFunction = (...args: any[]) => any;

export default component<WithFetchProps, WithFetchEvents, ScopedSlots>()({
  props: {
    apiCall: {},
    payload: {},
    returnIsLoading: {
      type: Boolean,
      default: () => false,
    },
    isCached: {
      type: Boolean,
      default: true,
    },
    autoUpdate: {},
    cacheRefreshOnUpdate: Boolean,
    hasNoContentPage: {
      type: Boolean,
      default: false,
    },
    hasErrorDialog: {
      type: Boolean,
      default: false,
    },
    fetchDelay: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      isLoading: true,
      error: undefined as undefined | ApiError,
      response: undefined as any,
      autoUpdateIntervalId: undefined as number | undefined,
      fetchTimeout: undefined as any,
    };
  },

  methods: {
    async fetch(payload: any[], autoUpdate?: boolean) {
      this.error = undefined;
      try {
        const apiCall = ((this.isCached && !autoUpdate ? cachedClient : client)[this.apiCall] as AnyFunction)(...payload);
        const response = await apiCall;

        if (this.cacheRefreshOnUpdate) {
          cachedClient.cache.writeFeed(JSON.stringify(payload), response);
        }

        this.$emit('load', response, !!autoUpdate);

        return response;
      } catch (error) {
        if (!(error instanceof ApiError)) {
          throw error;
        }
        this.error = error;
      }
    },

    setupAutoUpdate() {
      const updateObject = typeof this.autoUpdate === 'boolean' ? {} : this.autoUpdate!;

      const payload = updateObject.payload ?? this.payload ?? [];
      const interval = updateObject.interval ?? 30000;

      const autoUpdateFetch = async () => {
        this.response = await this.fetch(payload, true);
      };

      this.autoUpdateIntervalId = setInterval(autoUpdateFetch, interval);
    },
  },

  watch: {
    async payload() {
      this.isLoading = true;
      this.response = await this.fetch(this.payload ?? []);
      this.isLoading = false;
    },
  },

  activated() {
    if (this.autoUpdate && !this.autoUpdateIntervalId) {
      this.setupAutoUpdate();
    }
  },

  deactivated() {
    if (this.autoUpdateIntervalId) {
      clearInterval(this.autoUpdateIntervalId);
      this.autoUpdateIntervalId = undefined;
    }
  },

  async created(): Promise<void> {
    this.fetchTimeout = setTimeout(async () => {
      if (this.autoUpdate) {
        this.setupAutoUpdate();
      }
      this.response = await this.fetch(this.payload ?? []);
      this.isLoading = false;
    }, this.fetchDelay);
  },

  destroyed() {
    clearTimeout(this.fetchTimeout);
    if (this.autoUpdateIntervalId) {
      clearInterval(this.autoUpdateIntervalId);
    }
  },

  render() {
    return (
      <fragment>
        {this.error
          ? this.hasErrorDialog
            ? <Dialog />
            : this.$slots.error || null
          : !this.isLoading
            ? this.hasNoContentPage && (!this.response || Array.isArray(this.response) && !this.response.length)
              ? <NoContentPage />
              : this.$scopedSlots.loaded({ response: this.response })
            : this.returnIsLoading
              ? this.$scopedSlots.loaded({ response: undefined, isLoading: true })
              : this.$slots.loading || <div class='loader'><InlineLoader class='centered' /></div>}
      </fragment>
    );
  },
});

// VUE JSX HOT LOADER //
if (module.hot) require("/github/workspace/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: eval('this'), module: module, hotId: "_vue_jsx_hot-0fd88ffe/WithFetch.vue" });